import React, { useState, useCallback } from "react";
import useApiEp from "../../hooks/useApiEp";
import axios from "axios";
import {
  Container,
  Box,
  Typography,
  Button,
  IconButton,
  Chip,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tooltip,
  Collapse,
  Divider,
  Stack,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  PersonAdd as PersonAddIcon,
  RemoveCircleOutline as RemoveIcon,
  Groups as GroupsIcon,
} from "@mui/icons-material";
import { API_ENDPOINT } from "../../utils/constants";
import { getHeader } from "../../utils/util";

// ─── helpers ────────────────────────────────────────────────────────────────

const EMPTY_AGENT = { agentNumber: "", agentName: "" };
const VOICE = "/voice";

function stripCustomerId(groupName, customerId) {
  return groupName?.startsWith(String(customerId))
    ? groupName.slice(String(customerId).length)
    : groupName;
}

// ─── sub-components ─────────────────────────────────────────────────────────

function AgentRow({ agent }) {
  const isOnline = agent.loginStatus === 1;
  const onCall = agent.callStatus === 1;
  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1.5,
        py: 0.75,
        px: 1.5,
        borderRadius: 1,
        bgcolor: "background.paper",
        border: "1px solid",
        borderColor: "divider",
      }}
    >
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: "50%",
          bgcolor: isOnline
            ? onCall
              ? "warning.main"
              : "success.main"
            : "text.disabled",
          flexShrink: 0,
        }}
      />
      <Typography variant="body2" fontWeight={500} sx={{ flex: 1 }}>
        {agent.agentName}
      </Typography>
      <Typography variant="body2" color="text.secondary">
        {agent.agentNumber}
      </Typography>
      {onCall && (
        <Chip label="On Call" size="small" color="warning" variant="outlined" />
      )}
    </Box>
  );
}

function GroupCard({ group, customerId, onEdit, onDelete }) {
  const [expanded, setExpanded] = useState(false);
  const displayName = stripCustomerId(group.groupName, customerId);
  const agentCount = group.agents?.length ?? 0;

  return (
    <Box
      sx={{
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 2,
        overflow: "hidden",
        bgcolor: "background.paper",
        transition: "box-shadow 0.2s",
        "&:hover": { boxShadow: 3 },
      }}
    >
      {/* header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 2,
          py: 1.5,
          cursor: "pointer",
          userSelect: "none",
        }}
        onClick={() => setExpanded((v) => !v)}
      >
        <GroupsIcon color="primary" />
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography fontWeight={600} noWrap>
            {displayName}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {agentCount} agent{agentCount !== 1 ? "s" : ""} · ID {group.groupId}
          </Typography>
        </Box>

        <Chip
          size="small"
          label={group.groupStatus === 1 ? "Active" : "Inactive"}
          color={group.groupStatus === 1 ? "success" : "default"}
          variant="outlined"
        />

        <Tooltip title="Edit group">
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              onEdit(group);
            }}
          >
            <EditIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        <Tooltip title="Delete group">
          <IconButton
            size="small"
            color="error"
            onClick={(e) => {
              e.stopPropagation();
              onDelete(group);
            }}
          >
            <DeleteIcon fontSize="small" />
          </IconButton>
        </Tooltip>

        {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
      </Box>

      {/* agents list */}
      <Collapse in={expanded}>
        <Divider />
        <Box
          sx={{
            px: 2,
            py: 1.5,
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          {agentCount === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No agents in this group.
            </Typography>
          ) : (
            group.agents.map((a) => <AgentRow key={a.agentId} agent={a} />)
          )}
        </Box>
      </Collapse>
    </Box>
  );
}

// ─── Agent form fields inside dialog ────────────────────────────────────────

function AgentFieldList({ agents, onChange }) {
  const update = (i, field, value) => {
    const next = agents.map((a, idx) =>
      idx === i ? { ...a, [field]: value } : a
    );
    onChange(next);
  };
  const remove = (i) => onChange(agents.filter((_, idx) => idx !== i));
  const add = () => onChange([...agents, { ...EMPTY_AGENT }]);

  return (
    <Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
        <Typography variant="subtitle2">Agents</Typography>
        <Button size="small" startIcon={<PersonAddIcon />} onClick={add}>
          Add Agent
        </Button>
      </Box>
      <Stack spacing={1.5}>
        {agents.map((agent, i) => (
          <Box
            key={i}
            sx={{ display: "flex", gap: 1, alignItems: "flex-start" }}
          >
            <TextField
              label="Name"
              size="small"
              value={agent.agentName}
              onChange={(e) => update(i, "agentName", e.target.value)}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Number (10-digit)"
              size="small"
              value={agent.agentNumber}
              onChange={(e) => update(i, "agentNumber", e.target.value)}
              inputProps={{ maxLength: 10 }}
              error={
                agent.agentNumber.length > 0 &&
                !/^\d{10}$/.test(agent.agentNumber)
              }
              sx={{ flex: 1 }}
            />
            <Tooltip title="Remove">
              <IconButton
                size="small"
                color="error"
                onClick={() => remove(i)}
                disabled={agents.length === 1}
              >
                <RemoveIcon />
              </IconButton>
            </Tooltip>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}

// ─── Add / Edit Dialog ───────────────────────────────────────────────────────

function GroupDialog({ open, onClose, onSaved, editGroup, customerId }) {
  const isEdit = Boolean(editGroup);

  const [groupName, setGroupName] = useState("");
  const [agents, setAgents] = useState([{ ...EMPTY_AGENT }]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  // Populate form when editing
  React.useEffect(() => {
    if (open && isEdit) {
      setGroupName(stripCustomerId(editGroup.groupName, customerId));
      setAgents(
        editGroup.agents?.map((a) => ({
          agentId: a.agentId,
          agentName: a.agentName,
          agentNumber: a.agentNumber,
          groupId: a.groupId,
        })) ?? [{ ...EMPTY_AGENT }]
      );
    } else if (open) {
      setGroupName("");
      setAgents([{ ...EMPTY_AGENT }]);
    }
    setError("");
  }, [open]);

  const validate = () => {
    if (!groupName.trim()) return "Group name is required.";
    for (const a of agents) {
      if (!a.agentName?.trim()) return "All agents need a name.";
      if (!/^\d{10}$/.test(a.agentNumber))
        return "All agents need a valid 10-digit number.";
    }
    return null;
  };

  const handleSave = async () => {
    const err = validate();
    if (err) {
      setError(err);
      return;
    }

    setSaving(true);
    setError("");
    try {
      if (isEdit) {
        await axios.put(
          `${API_ENDPOINT}/voice/agent-groups/${editGroup.groupId}`,
          {
            groupName: groupName.trim(),
            agents,
          },
          getHeader()
        );
      } else {
        await axios.post(
          `${API_ENDPOINT}/voice/agent-groups`,
          {
            groupName: groupName.trim(),
            agents,
          },
          getHeader()
        );
      }
      onSaved();
    } catch (e) {
      setError(
        e?.response?.data?.message ?? "Something went wrong. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        {isEdit ? "Edit Agent Group" : "Add Agent Group"}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ mt: 0.5 }}>
          {error && <Alert severity="error">{error}</Alert>}
          <TextField
            label="Group Name"
            value={groupName}
            onChange={(e) => setGroupName(e.target.value)}
            fullWidth
            size="small"
            autoFocus
          />
          <AgentFieldList agents={agents} onChange={setAgents} />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={14} /> : null}
        >
          {isEdit ? "Save Changes" : "Create Group"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Delete Confirmation ─────────────────────────────────────────────────────

function DeleteDialog({ open, onClose, onDeleted, group, customerId }) {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await axios.delete(
        `${API_ENDPOINT}/voice/agent-groups/${group.groupId}`,
        getHeader()
      );
      onDeleted();
    } catch (e) {
      setError(
        e?.response?.data?.message ?? "Failed to delete. Please try again."
      );
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle>Delete Group</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 1.5 }}>
            {error}
          </Alert>
        )}
        <Typography>
          Are you sure you want to delete{" "}
          <strong>{stripCustomerId(group?.groupName, customerId)}</strong>? This
          action cannot be undone.
        </Typography>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} disabled={deleting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          color="error"
          onClick={handleDelete}
          disabled={deleting}
          startIcon={deleting ? <CircularProgress size={14} /> : null}
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// ─── Main Component ──────────────────────────────────────────────────────────

const AgentsList = ({ customerId }) => {
  const { data, isLoading, isError, error, refetch } =
    useApiEp(`/voice/agent-groups`);

  const [addOpen, setAddOpen] = useState(false);
  const [editTarget, setEditTarget] = useState(null); // group object
  const [deleteTarget, setDeleteTarget] = useState(null); // group object

  const groups = Array.isArray(data) ? data : [];

  const handleSaved = useCallback(() => {
    setAddOpen(false);
    setEditTarget(null);
    refetch?.();
  }, [refetch]);

  const handleDeleted = useCallback(() => {
    setDeleteTarget(null);
    refetch?.();
  }, [refetch]);

  return (
    <Container maxWidth="md" sx={{ py: 3 }}>
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 3,
        }}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            Agent Groups
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {groups.length} group{groups.length !== 1 ? "s" : ""} configured
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAddOpen(true)}
        >
          Add Group
        </Button>
      </Box>

      {/* States */}
      {isLoading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {isError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error?.message ?? "Failed to load agent groups."}
        </Alert>
      )}

      {!isLoading && !isError && groups.length === 0 && (
        <Box
          sx={{
            textAlign: "center",
            py: 8,
            border: "2px dashed",
            borderColor: "divider",
            borderRadius: 3,
          }}
        >
          <GroupsIcon sx={{ fontSize: 48, color: "text.disabled", mb: 1 }} />
          <Typography color="text.secondary">No agent groups yet.</Typography>
          <Button
            sx={{ mt: 2 }}
            startIcon={<AddIcon />}
            onClick={() => setAddOpen(true)}
          >
            Create your first group
          </Button>
        </Box>
      )}

      {/* Group cards */}
      {!isLoading && !isError && (
        <Stack spacing={1.5}>
          {groups.map((group) => (
            <GroupCard
              key={group.groupId}
              group={group}
              customerId={customerId}
              onEdit={setEditTarget}
              onDelete={setDeleteTarget}
            />
          ))}
        </Stack>
      )}

      {/* Add dialog */}
      <GroupDialog
        open={addOpen}
        onClose={() => setAddOpen(false)}
        onSaved={handleSaved}
        editGroup={null}
        customerId={customerId}
      />

      {/* Edit dialog */}
      {editTarget && (
        <GroupDialog
          open={Boolean(editTarget)}
          onClose={() => setEditTarget(null)}
          onSaved={handleSaved}
          editGroup={editTarget}
          customerId={customerId}
        />
      )}

      {/* Delete dialog */}
      {deleteTarget && (
        <DeleteDialog
          open={Boolean(deleteTarget)}
          onClose={() => setDeleteTarget(null)}
          onDeleted={handleDeleted}
          group={deleteTarget}
          customerId={customerId}
        />
      )}
    </Container>
  );
};

export default AgentsList;
